/**
 * Top-down vehicle marker for the map.
 *
 * Two structural decisions worth recording, because in both cases the obvious
 * choice was the wrong one:
 *
 * 1. **Top-down, not isometric.** A marker has to rotate to show heading. An
 *    isometric render has its camera angle baked into the pixels, so rotating
 *    one makes the car appear to lie down and drive into the screen at 180°.
 *    A bird's-eye view is an orthographic projection from above, so rotating
 *    it is geometrically what actually happens. This is why every mapping
 *    product that shows vehicle heading uses a top-down car. Depth comes from
 *    shading, not from the camera.
 *
 * 2. **A data URI, not a Symbol path.** google.maps honours `rotation` only on
 *    Symbol icons, which are a single path in a single colour. A car with
 *    glass, wheels, panel seams and lights needs several, so the rotation is
 *    baked into the SVG per heading instead.
 *
 * What makes it read as a photographed car rather than an illustration:
 *
 *  - **Proportion.** 50 long by 20 wide, a 2.5 ratio, which is roughly a real
 *    sedan (4.7 m by 1.85 m). The first version was 2.0 and that alone made it
 *    look like a toy.
 *  - **Curvature across the width.** A gradient runs flank-to-flank: bright
 *    along the crown of the roof where sheet metal faces the sky, falling to
 *    near-black where it curves away. This is the single biggest cue, and it
 *    is defined across the *car*, not the screen, so it stays correct at every
 *    heading.
 *  - **The roof is body colour.** Looking straight down you see hood, glass,
 *    roof, glass, boot — only the two windows are dark. Blacking out the whole
 *    greenhouse is what gives cartoon markers their sticker look.
 *  - **Panel seams.** Hair-thin darker lines at the bonnet and boot shuts and
 *    along the door tops. Barely visible, and the thing that reads as panels
 *    rather than one moulded shape.
 *  - **No outline.** A drawn keyline is a cartoon convention. Contrast against
 *    the basemap comes from a soft contact shadow plus a very faint edge.
 */

// Square canvas: the car's diagonal is √(50² + 20²) ≈ 54, and rotation happens
// about the centre, so the box has to be able to hold that at 45°.
const CANVAS = 64;

// 5° buckets — 72 sprites per colour rather than 360. Below about 3° the
// difference is invisible at marker size, and each distinct value is a string
// built and held for the life of the page.
const HEADING_BUCKET = 5;

// Front points along -Y (north at heading 0), matching the arrow this replaced,
// so `rotation = heading` needs no offset. Written out explicitly and
// symmetrically about x=32 rather than as terse relative curves — the shape is
// the one thing here that cannot be checked by reading the numbers.
const BODY = [
  "M 32 7",
  "C 28.6 7 25.6 7.9 24.4 9.6",
  "C 23.1 11.6 22.4 14.2 22.2 17.5",
  "L 22 26",
  "L 22 44",
  "C 22 49 22.6 53 23.8 55",
  "C 25 56.6 28.2 57 32 57",
  "C 35.8 57 39 56.6 40.2 55",
  "C 41.4 53 42 49 42 44",
  "L 42 26",
  "L 41.8 17.5",
  "C 41.6 14.2 40.9 11.6 39.6 9.6",
  "C 38.4 7.9 35.4 7 32 7",
  "Z",
].join(" ");

const spriteCache = new Map();

function carSvg(color, heading) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CANVAS} ${CANVAS}" width="${CANVAS}" height="${CANVAS}">
<defs>
<radialGradient id="sh" cx=".5" cy=".5" r=".5">
<stop offset=".52" stop-color="#000" stop-opacity=".34"/>
<stop offset=".78" stop-color="#000" stop-opacity=".14"/>
<stop offset="1" stop-color="#000" stop-opacity="0"/>
</radialGradient>
<linearGradient id="cw" x1="0" y1="0" x2="1" y2="0">
<stop offset="0" stop-color="#000" stop-opacity=".44"/>
<stop offset=".14" stop-color="#000" stop-opacity=".2"/>
<stop offset=".3" stop-color="#fff" stop-opacity=".1"/>
<stop offset=".44" stop-color="#fff" stop-opacity=".26"/>
<stop offset=".58" stop-color="#fff" stop-opacity=".1"/>
<stop offset=".76" stop-color="#000" stop-opacity=".16"/>
<stop offset="1" stop-color="#000" stop-opacity=".46"/>
</linearGradient>
<linearGradient id="cl" x1="0" y1="0" x2="0" y2="1">
<stop offset="0" stop-color="#fff" stop-opacity=".16"/>
<stop offset=".16" stop-color="#fff" stop-opacity="0"/>
<stop offset=".84" stop-color="#000" stop-opacity="0"/>
<stop offset="1" stop-color="#000" stop-opacity=".2"/>
</linearGradient>
<linearGradient id="ws" x1="0" y1="0" x2=".7" y2="1">
<stop offset="0" stop-color="#9fc4e8" stop-opacity=".5"/>
<stop offset=".45" stop-color="#2b3a4d" stop-opacity=".85"/>
<stop offset="1" stop-color="#141c26" stop-opacity=".95"/>
</linearGradient>
<clipPath id="cp"><path d="${BODY}"/></clipPath>
</defs>
<g transform="rotate(${heading} 32 32)">
<ellipse cx="32" cy="32.4" rx="14.6" ry="27" fill="url(#sh)"/>
<g fill="#0f0f12">
<rect x="21" y="16.4" width="2.7" height="8.2" rx="1"/>
<rect x="40.3" y="16.4" width="2.7" height="8.2" rx="1"/>
<rect x="21" y="39.8" width="2.7" height="8.8" rx="1"/>
<rect x="40.3" y="39.8" width="2.7" height="8.8" rx="1"/>
</g>
<path d="${BODY}" fill="${color}"/>
<g clip-path="url(#cp)">
<path d="${BODY}" fill="url(#cw)"/>
<path d="${BODY}" fill="url(#cl)"/>
<path d="M 27.4 21.6 C 29.1 20.9 34.9 20.9 36.6 21.6 L 38 27.2 L 26 27.2 Z" fill="url(#ws)"/>
<path d="M 28.4 22 L 31.4 22 L 28.9 26.9 L 26.4 26.9 Z" fill="#fff" opacity=".14"/>
<path d="M 26.2 39 H 37.8 L 36.6 43.1 C 34.9 43.7 29.1 43.7 27.4 43.1 Z" fill="#1a222d" opacity=".92"/>
<path d="M 27.6 39.4 H 31 L 29.6 42.6 H 27.2 Z" fill="#fff" opacity=".09"/>
<g fill="#0b0e13" opacity=".7">
<rect x="24.5" y="27.3" width="1.5" height="11.4" rx=".6"/>
<rect x="38" y="27.3" width="1.5" height="11.4" rx=".6"/>
</g>
<g stroke="#000" fill="none" stroke-width=".55" opacity=".22">
<path d="M 23.2 20.9 H 40.8"/>
<path d="M 23.6 45.9 H 40.4"/>
<path d="M 23.3 21.6 V 46"/>
<path d="M 40.7 21.6 V 46"/>
<path d="M 24.4 33.6 H 39.6"/>
</g>
<g fill="#fff8e2" opacity=".82">
<path d="M 24.6 9.9 C 26.4 9.5 28.4 9.4 29.6 9.5 L 29.4 11.6 L 24.2 11.9 Z"/>
<path d="M 39.4 9.9 C 37.6 9.5 35.6 9.4 34.4 9.5 L 34.6 11.6 L 39.8 11.9 Z"/>
</g>
<rect x="29.9" y="9.6" width="4.2" height="1.7" rx=".5" fill="#0c0c10" opacity=".45"/>
<g fill="#c8202c" opacity=".85">
<path d="M 24 53.5 H 28.8 L 29 55.3 H 24.4 Z"/>
<path d="M 40 53.5 H 35.2 L 35 55.3 H 39.6 Z"/>
</g>
<rect x="29.4" y="54.2" width="5.2" height="1.3" rx=".5" fill="#0c0c10" opacity=".3"/>
</g>
<path d="${BODY}" fill="none" stroke="#0a0a0c" stroke-width=".9" opacity=".45"/>
<g fill="${color}" opacity=".95">
<path d="M 22.2 26.2 L 20.5 26.9 L 20.8 28.3 L 22.4 28 Z"/>
<path d="M 41.8 26.2 L 43.5 26.9 L 43.2 28.3 L 41.6 28 Z"/>
</g>
</g>
</svg>`;
}

/** Data URI for a car sprite, memoised per (colour, heading bucket). */
export function carSpriteUrl(color, heading = 0) {
  const normalized = ((Number(heading) || 0) % 360 + 360) % 360;
  const bucket = (Math.round(normalized / HEADING_BUCKET) * HEADING_BUCKET) % 360;
  const key = `${color}:${bucket}`;
  let url = spriteCache.get(key);
  if (!url) {
    url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(carSvg(color, bucket))}`;
    spriteCache.set(key, url);
  }
  return url;
}

/**
 * google.maps icon for a vehicle. `size` is the rendered box in pixels; the car
 * occupies about three quarters of it, the rest being the contact shadow that
 * keeps the marker readable over satellite imagery.
 */
export function carIcon(maps, { color, heading = 0, size = 38 }) {
  return {
    url: carSpriteUrl(color, heading),
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
}
